import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { ReportDocument } from '../types/report';
import { getReport } from '../services/reportService';
import { resolveSection } from '../components/report/sections/sectionRegistry';
import { ReportToc } from '../components/report/ReportToc';
import { ReportPanelProvider, useReportPanel } from '../state/reportPanel';
import { DrillPanel } from '../components/report/DrillPanel';
import Layout from '../components/Layout';
import '../components/report/report.css';

export default function ReportPage() {
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<ReportDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    getReport(id)
      .then((d) => active && setDoc(d))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [id]);

  if (error)
    return (
      <Layout>
        <div className="p-8 text-red-600">Failed to load report: {error}</div>
      </Layout>
    );
  if (!doc) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report…</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout className="report-layout-main">
      <ReportPanelProvider>
        <div className="report-root report-embedded">
          <div className="report-shell">
            <aside className="report-toc-col">
              <Link to="/reports" className="report-back">
                <ArrowLeft size={15} /> All reports
              </Link>
              {doc.sections.length > 0 && <ReportToc sections={doc.sections} />}
            </aside>

            <div className="report-inner">
              {/* The cover slide carries the title; only show the plain H1 header
                when there is no cover section. */}
              {!doc.sections.some((s) => s.kind === 'cover') && (
                <header>
                  <h1 className="report-h1">{doc.title}</h1>
                  {doc.subtitle && <p className="report-sub">{doc.subtitle}</p>}
                </header>
              )}

              {doc.sections.map((section) => {
                const SectionComp = resolveSection(section.kind);
                return <SectionComp key={section.id} section={section} claims={doc.claims} />;
              })}
            </div>
          </div>
        </div>
        {/* reportId comes from the URL param — the stored document's own `id`
          field is empty (not written back into the JSON at assemble time). */}
        <DrillPanel reportId={id ?? doc.id} claims={doc.claims} />
        <FloatingAsk />
      </ReportPanelProvider>
    </Layout>
  );
}

/** Floating "Ask" button (mock .floating-ask) — opens the chat panel unscoped.
 * Hidden while the panel is already open. */
function FloatingAsk() {
  const { activeClaimId, openChat } = useReportPanel();
  if (activeClaimId !== null) return null;
  return (
    <button type="button" className="floating-ask" onClick={() => openChat(null)}>
      ✦ Ask about this report
    </button>
  );
}
